import { signal } from "@preact/signals";
import type { ProfileMeta } from "@/state/profiles";

/**
 * Cloud saves through Azure Static Web Apps: the platform's built-in login (`/.auth/*`) identifies
 * the player and the managed API (`/api/saves`) stores one blob per hero. When the site runs
 * without the platform (local dev, or the API not configured) everything silently stays local.
 */

export interface CloudUser {
  provider: string;
  userId: string;
  name: string;
}

export type CloudSaveMeta = ProfileMeta;

/** undefined = not checked yet, null = not logged in */
export const cloudUser = signal<CloudUser | null | undefined>(undefined);
/** True when the site runs on Azure Static Web Apps (the auth endpoint answered with JSON). */
export const cloudAvailable = signal(false);
export const cloudBusy = signal(false);

export function loginUrl(provider: "aad" | "github"): string {
  return `/.auth/login/${provider}?post_login_redirect_uri=${encodeURIComponent(location.pathname)}`;
}

export const LOGOUT_URL = "/.auth/logout?post_logout_redirect_uri=/";

async function jsonOrNull<T>(res: Response): Promise<T | null> {
  if (!res.ok) return null;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("json")) return null;
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function fetchCloudUser(): Promise<CloudUser | null> {
  try {
    const res = await fetch("/.auth/me", { headers: { Accept: "application/json" } });
    const body = await jsonOrNull<{
      clientPrincipal: { identityProvider: string; userId: string; userDetails: string } | null;
    }>(res);
    cloudAvailable.value = body !== null;
    const p = body?.clientPrincipal;
    const user = p ? { provider: p.identityProvider, userId: p.userId, name: p.userDetails } : null;
    cloudUser.value = user;
    return user;
  } catch {
    cloudUser.value = null;
    return null;
  }
}

export async function listCloudSaves(): Promise<CloudSaveMeta[]> {
  const res = await fetch("/api/saves", { headers: { Accept: "application/json" } });
  return (await jsonOrNull<CloudSaveMeta[]>(res)) ?? [];
}

export async function downloadCloudSave(id: string): Promise<string | null> {
  const res = await fetch(`/api/saves/${encodeURIComponent(id)}`, {
    headers: { Accept: "application/json" },
  });
  const body = await jsonOrNull<{ meta: CloudSaveMeta; save: unknown }>(res);
  return body?.save ? JSON.stringify(body.save) : null;
}

export async function uploadCloudSave(id: string, json: string, meta: ProfileMeta): Promise<boolean> {
  try {
    const res = await fetch(`/api/saves/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ meta, save: JSON.parse(json) }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteCloudSave(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/saves/${encodeURIComponent(id)}`, { method: "DELETE" });
    return res.ok;
  } catch {
    return false;
  }
}

const pending = new Map<string, ReturnType<typeof setTimeout>>();

/** Upload a save a few seconds after the last local save, only when logged in. */
export function scheduleCloudUpload(id: string, json: string, meta: ProfileMeta): void {
  if (!cloudUser.value) return;
  const prev = pending.get(id);
  if (prev) clearTimeout(prev);
  pending.set(
    id,
    setTimeout(() => {
      pending.delete(id);
      void uploadCloudSave(id, json, meta);
    }, 4000),
  );
}

/**
 * Pull cloud heroes into the local profile list: profiles missing locally, or newer in the cloud,
 * are downloaded. Returns how many were updated.
 */
export async function syncFromCloud(
  local: ProfileMeta[],
  importSave: (id: string, json: string) => ProfileMeta | null,
): Promise<number> {
  if (!cloudUser.value) return 0;
  cloudBusy.value = true;
  try {
    const remote = await listCloudSaves();
    let updated = 0;
    for (const r of remote) {
      const mine = local.find((p) => p.id === r.id);
      if (mine && mine.updatedAt >= r.updatedAt) continue;
      const json = await downloadCloudSave(r.id);
      if (json && importSave(r.id, json)) updated++;
    }
    // push local heroes the cloud does not have yet
    for (const p of local) {
      if (!remote.some((r) => r.id === p.id)) {
        const json = localStorage.getItem(`mathrpg.save.${p.id}`);
        if (json) void uploadCloudSave(p.id, json, p);
      }
    }
    return updated;
  } catch {
    return 0;
  } finally {
    cloudBusy.value = false;
  }
}
